import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './api';
import { toast } from '../utils/toast';
import { extractApiError } from '../utils/apiError';

/** Fetch a user's profile by numeric ID (authenticated route). */
export const useGetUserProfile = (userId: string | number | undefined, enabled = true) => {
  return useQuery({
    queryKey: ['profile', userId],
    queryFn: async () => {
      if (!userId) throw new Error('User ID is required');
      console.log(`[API] Fetching profile for userId: ${userId} via /profiles/${userId}/`);
      const response = await apiClient.get(`/profiles/${userId}/`);
      console.log(`[API] Profile response for ${userId}:`, response.data);
      return response.data;
    },
    enabled: !!userId && enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/** Fetch a public profile by user ID or username (no auth needed). */
export const useGetPublicProfile = (identifier: string | number | undefined) => {
  return useQuery({
    queryKey: ['public-profile', identifier],
    queryFn: async () => {
      if (!identifier) throw new Error('Identifier is required');
      console.log(
        `[API] Fetching public profile for identifier: ${identifier} via /public/profiles/${identifier}/`,
      );
      const response = await apiClient.get(`/public/profiles/${identifier}/`);
      // Handle envelopes: { profile: {...} } | { data: {...} } | direct object
      const data = response.data?.profile ?? response.data?.data ?? response.data;
      console.log(`[API] Public profile response for ${identifier}:`, data);
      return data;
    },
    enabled: !!identifier,
    staleTime: 5 * 60 * 1000,
  });
};

export interface EditProfilePayload {
  full_name?: string;
  username?: string;
  bio?: string;
  gender?: string;
  date_of_birth?: string;
  phone_number?: string;
  spotify?: string | null;
  apple_music?: string | null;
  soundcloud?: string | null;
  amazon_music?: string | null;
  youtube_music?: string | null;
  instagram?: string | null;
  twitter?: string | null;
  tiktok?: string | null;
  website?: string | null;
  // Avatar / cover uploaded as Cloudinary URL strings
  prof_pics?: string | null;
  cover_photo?: string | null;
  remove_prof_pics?: boolean;
  remove_cover_photo?: boolean;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  user_types?: Array<{
    name: string;
    tags: string[];
  }>;
  genres?: string[];
  subscriptions_enabled?: boolean;
}

/** Update the authenticated user's profile. Supports JSON or FormData (for files). */
export const useEditProfileMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: EditProfilePayload | FormData) => {
      console.log('[API] Patching profile via /custom_account/update-profile/');
      if (payload instanceof FormData) {
        console.log('[API] Payload is FormData (Multipart)');
        // Log FormData keys for debugging
        for (const [key, value] of (payload as any).entries()) {
          console.log(`  - ${key}:`, value instanceof File ? `File(${value.name})` : value);
        }
      } else {
        console.log('[API] Payload is JSON:', payload);
      }

      const { data } = await apiClient.patch(
        '/custom_account/update-profile/',
        payload instanceof FormData ? payload : JSON.stringify(payload),
        {
          headers: {
            'Content-Type':
              payload instanceof FormData ? 'multipart/form-data' : 'application/json',
          },
        },
      );
      console.log('[API] Edit Profile response:', data);
      return data;
    },
    onSuccess: () => {
      toast.success('Profile updated successfully', { duration: 4000 });
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      queryClient.invalidateQueries({ queryKey: ['public-profile'] });
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
    },
    onError: (err: any) => {
      toast.error(extractApiError(err, 'Profile update failed'));
    },
  });
};

/** Follow User */
export const useFollowUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId }: { userId: string | number; username?: string }) => {
      console.log(`[API] Following user ${userId} via /profiles/follow/${userId}/`);
      const { data } = await apiClient.post(`/profiles/follow/${userId}/`);
      console.log(`[API] Follow response for ${userId}:`, data);
      return data;
    },
    onSuccess: (_, { userId, username }) => {
      toast.success('User followed');
      queryClient.invalidateQueries({ queryKey: ['profile', userId] });
      queryClient.invalidateQueries({ queryKey: ['public-profile', userId] });
      if (username) {
        queryClient.invalidateQueries({ queryKey: ['public-profile', username] });
      }
      queryClient.invalidateQueries({ queryKey: ['events', 'detail'] });
      queryClient.invalidateQueries({ queryKey: ['events', 'public', 'detail'] });
    },
    onError: (err: any) => {
      toast.error(extractApiError(err, 'Failed to follow user'));
    },
  });
};

/** Unfollow User */
export const useUnfollowUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId }: { userId: string | number; username?: string }) => {
      console.log(`[API] Unfollowing user ${userId} via POST /profiles/unfollow/${userId}/`);
      const { data } = await apiClient.post(`/profiles/unfollow/${userId}/`);
      console.log(`[API] Unfollow response for ${userId}:`, data);
      return data;
    },
    onSuccess: (_, { userId, username }) => {
      toast.success('User unfollowed');
      queryClient.invalidateQueries({ queryKey: ['profile', userId] });
      queryClient.invalidateQueries({ queryKey: ['public-profile', userId] });
      if (username) {
        queryClient.invalidateQueries({ queryKey: ['public-profile', username] });
      }
      queryClient.invalidateQueries({ queryKey: ['events', 'detail'] });
      queryClient.invalidateQueries({ queryKey: ['events', 'public', 'detail'] });
    },
    onError: (err: any) => {
      toast.error(extractApiError(err, 'Failed to unfollow user'));
    },
  });
};

/** Subscribe to a creator's mailing list */
export const useSubscribeToCreator = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId }: { userId: string | number; username?: string }) => {
      console.log(
        `[API] Subscribing to user ${userId} mailing list via POST /profiles/${userId}/subscriptions/`,
      );
      const { data } = await apiClient.post(`/profiles/${userId}/subscriptions/`);
      console.log(`[API] Subscribe response for ${userId}:`, data);
      return data;
    },
    onSuccess: (_, { userId, username }) => {
      toast.success('Successfully subscribed to mailing list');
      queryClient.invalidateQueries({ queryKey: ['profile', userId] });
      queryClient.invalidateQueries({ queryKey: ['public-profile', userId] });
      if (username) {
        queryClient.invalidateQueries({ queryKey: ['public-profile', username] });
      }
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
    },
    onError: (err: any) => {
      toast.error(extractApiError(err, 'Unable to subscribe at this time.'));
    },
  });
};

/** Unsubscribe from a creator's mailing list */
export const useUnsubscribeFromCreator = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId }: { userId: string | number; username?: string }) => {
      console.log(
        `[API] Unsubscribing from user ${userId} mailing list via DELETE /profiles/${userId}/subscriptions/`,
      );
      const { data } = await apiClient.delete(`/profiles/${userId}/subscriptions/`);
      console.log(`[API] Unsubscribe response for ${userId}:`, data);
      return data;
    },
    onSuccess: (_, { userId, username }) => {
      toast.success('Successfully unsubscribed from mailing list');
      queryClient.invalidateQueries({ queryKey: ['profile', userId] });
      queryClient.invalidateQueries({ queryKey: ['public-profile', userId] });
      if (username) {
        queryClient.invalidateQueries({ queryKey: ['public-profile', username] });
      }
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
    },
    onError: (err: any) => {
      toast.error(extractApiError(err, 'Failed to unsubscribe'));
    },
  });
};

/**
 * Point #13: Check username availability
 * GET /custom_account/username-availability/?username={username}
 */
export const useUsernameAvailability = (username: string) => {
  return useQuery<{ available: boolean; message?: string }>({
    queryKey: ['username-availability', username],
    queryFn: async () => {
      if (!username || username.length < 3) return { available: false, message: 'Too short' };
      console.log(
        `[API] Checking username availability for "${username}" via /custom_account/username-availability/`,
      );
      const { data } = await apiClient.get(
        `/custom_account/username-availability/?username=${encodeURIComponent(username)}`,
      );
      console.log(`[API] Username availability response for "${username}":`, data);
      return data;
    },
    enabled: !!username && username.length >= 3,
    staleTime: 0, // Always check fresh
  });
};

/** Point #6: Home Feed */
export const useGetHomeFeed = (page = 1, pageSize = 20) => {
  return useQuery({
    queryKey: ['home-feed', page, pageSize],
    queryFn: async () => {
      console.log(`[API] Fetching home feed via /profiles/home/?page=${page}`);
      const { data } = await apiClient.get(`/profiles/home/?page=${page}&page_size=${pageSize}`);
      console.log('[API] Home feed response:', data);
      return data;
    },
  });
};

/** Point #7: Stripe Account Details */
export const useGetStripeDetails = () => {
  return useQuery({
    queryKey: ['stripe-details'],
    queryFn: async () => {
      console.log('[API] Fetching Stripe details via /profiles/stripe-account-detail/');
      const { data } = await apiClient.get('/profiles/stripe-account-detail/');
      console.log('[API] Stripe details response:', data);
      return data;
    },
  });
};

/** Point #10: Preferred Locations - List */
export const useGetPreferredLocations = () => {
  return useQuery({
    queryKey: ['preferred-locations'],
    queryFn: async () => {
      console.log('[API] Fetching preferred locations via /profiles/preferred-locations/');
      const { data } = await apiClient.get('/profiles/preferred-locations/');
      console.log('[API] Preferred locations response:', data);
      return data;
    },
  });
};

/** Point #10: Preferred Locations - Create */
export const useAddPreferredLocation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { city: string; state: string; country?: string }) => {
      console.log(
        '[API] Adding preferred location via /profiles/preferred-locations/create/:',
        payload,
      );
      const { data } = await apiClient.post('/profiles/preferred-locations/create/', payload);
      console.log('[API] Add location response:', data);
      return data;
    },
    onSuccess: () => {
      toast.success('Location added');
      queryClient.invalidateQueries({ queryKey: ['preferred-locations'] });
    },
    onError: (err: any) => {
      toast.error(extractApiError(err, 'Failed to add location'));
    },
  });
};

/** Point #10: Preferred Locations - Delete */
export const useDeletePreferredLocation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ city, state }: { city: string; state: string }) => {
      console.log(
        `[API] Deleting preferred location ${city}, ${state} via /profiles/preferred-locations/delete/${city}/${state}/`,
      );
      const { data } = await apiClient.delete(
        `/profiles/preferred-locations/delete/${city}/${state}/`,
      );
      console.log('[API] Delete location response:', data);
      return data;
    },
    onSuccess: () => {
      toast.success('Location removed');
      queryClient.invalidateQueries({ queryKey: ['preferred-locations'] });
    },
    onError: (err: any) => {
      toast.error(extractApiError(err, 'Failed to remove location'));
    },
  });
};

/** Point #12: Device Registration */
export const useRegisterDevice = () => {
  return useMutation({
    mutationFn: async (payload: { device_token: string; device_type: 'ios' | 'android' }) => {
      console.log('[API] Registering device via /profiles/devices-register/:', payload);
      const { data } = await apiClient.post('/profiles/devices-register/', payload);
      console.log('[API] Device registration response:', data);
      return data;
    },
  });
};
